import React, { useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Stack,
  TableHead,
  Checkbox,
  TablePagination,
  TableRow,
  Typography,
  TextField,
  Breadcrumbs,
  Link,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import { useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Resizable } from "re-resizable";

const TableComponentPending = ({
  FiltersJsx,
  columns,
  rows,
  CountryJsx,
  handleRowDoubleClick,
  handleSearchChange,
  handleChangePage,
  handleChangeRowsPerPage,
  handleClick,
  handleHomeClick,
  handleLocationclick,
  filteredRows,
  title,
  titleMaster,
  handleIconClick,
  isEditMode,
  setIsEditMode,
  searchTerms,
  selectedRows,
  setSelectedRows,
  onCellEdit,
  onSave,
  editable,
  onCancel,
  isLoading,
  handleDeleteClick,
  onSelectRow,
  breadcrumbsButton,
}) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const [selected, setSelected] = React.useState([]);
  const location = useLocation();
  const permissions = location.state?.permissions || [];

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = filteredRows.map(row => row.BillCycle);
      setSelectedRows(newSelected);
      // Call parent component's onSelectRow with all row data
      if (onSelectRow) onSelectRow(filteredRows);
    } else {
      setSelectedRows([]);
      if (onSelectRow) onSelectRow([]);
    }
  };

  const handleCheckboxClick = (event, BillCycle) => {
    event.stopPropagation();

    const selectedIndex = selectedRows.indexOf(BillCycle);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selectedRows, BillCycle];
    } else {
      newSelected = selectedRows.filter(item => item !== BillCycle);
    }

    setSelectedRows(newSelected);

    // Get the complete row data for selected items
    const selectedRowsData = filteredRows.filter(row =>
      newSelected.includes(row.BillCycle)
    );

    // Call parent component's onSelectRow with selected row data
    if (onSelectRow) onSelectRow(selectedRowsData);
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleSaveClick = () => {
    onSave();
  };
  const handleCancelClick = () => {
    setIsEditMode(false);
    setSelectedRows([]); // Clear selected rows
    onCancel(); // Call cancel callback
  };

  const isSelected = (BillCycle) => selectedRows.includes(BillCycle);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLocationNavigation = () => {
    handleLocationclick(permissions);
  };

  useEffect(() => {
    // Sync the BookNo filter if it's part of the searchTerms
  }, [searchTerms]);

  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);

  const isCellEditable = (rowIndex, columnId, value) => {
    if (!isEditMode || !selectedRows.includes(rowIndex)) return false;
  };

  const handleCellChange = (event, rowIndex, columnId, currentValue) => {
    const newValue = event.target.value.toUpperCase();

    if (newValue === '') {
      onCellEdit(rowIndex, columnId, '', currentValue);
      return;
    }
    if (['A', 'M', 'R'].includes(newValue)) {
      onCellEdit(rowIndex, columnId, newValue, currentValue);
    }
  };

  const getCellBackgroundColor = (value, originalValue) => {
    // Use originalValue if value is empty
    const effectiveValue = value || originalValue;
    if (!effectiveValue) return null;

    const upperValue = String(effectiveValue).toUpperCase();

    switch (upperValue) {
      case 'A':
        return '#046A38';
      case 'C':
        return '#c971bb';
      case 'B':
        return '#635bff';
      case 'M':
        return '#800000';
      case 'R':
        return '#47a1ba';
      case 'P':
        return '#FF671F';
      default:
        return null;
    }
  };

  // Inside TableComponent's renderCell function, update the alignment based on column configuration
  const renderCell = (rowIndex, columnId, value, originalValue, align) => {
    if (isCellEditable(rowIndex, columnId, value)) {
      const backgroundColor = getCellBackgroundColor(value, originalValue);
      return (
        <TextField
          value={value || ''}
          onChange={(e) => handleCellChange(e, rowIndex, columnId, originalValue)}
          size="small"
          inputProps={{
            style: {
              textAlign: align || 'center', // Use column alignment
              padding: '0.1px',
              fontSize: '12px',
              backgroundColor: backgroundColor,
              color: backgroundColor ? '#ffffff' : 'inherit',
            },
            maxLength: 1
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
              width: '30px',
              height: '25px'
            }
          }}
        />
      );
    }

    const backgroundColor = getCellBackgroundColor(value, originalValue);
    return (
      <div style={{
        backgroundColor,
        color: backgroundColor ? '#ffffff' : 'inherit',
        padding: '0px',
        fontWeight: backgroundColor ? 'bold' : 'normal',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: align === 'left' ? 'flex-start' :
          align === 'right' ? 'flex-end' : 'center', // Align based on column config
        paddingLeft: align === 'left' ? '8px' : '0px', // Add padding for left-aligned text
        paddingRight: align === 'right' ? '8px' : '0px', // Add padding for right-aligned text
      }}>
        {value || ''}
      </div>
    );
  };

  return (
    <Box
      sx={{
        maxWidth: "95vw",
        overflowX: "auto",
        backgroundColor: "#f9f9f9",
        minHeight: "65vh",
      }}
    >
      {/* <ToastContainer /> */}
      <Box
        sx={{
          backgroundColor: "#ffffff",
          padding: "15px 22px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          borderRadius: "12px",
          margin: "10px auto",
          maxWidth: "95%",
          flexWrap: 'wrap', 
          gap: 2,         
        }}
      >
        {/* Breadcrumbs and Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "nowrap",
            minWidth: 'fit-content',
            gap: 2,
            flexGrow: 1,
          }}
        >
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="›"
            sx={{ fontSize: "16px", flexShrink: 0 }}
          >
            <Link
              onClick={handleHomeClick}
              underline="hover"
              color="#635bff"
              sx={{ cursor: "pointer" }}
            >
              Home
            </Link>

            <Typography
              color="#635bff"
              onClick={handleLocationclick}
              sx={{ cursor: "pointer", fontWeight: "bold" }}
            >
              {title}
            </Typography>

            <Typography color="text.primary" sx={{ fontWeight: "bold" }}>
              {titleMaster}
            </Typography>
          </Breadcrumbs>

          <Box sx={{ ml: 'auto', flexShrink: 0 }}>
            {breadcrumbsButton}
          </Box>
        </Box>

        {/* Filters */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "16px",
            minWidth: "300px",
            flexWrap: "nowrap",
            flexGrow: 1,
          }}
        >
          {FiltersJsx}
        </Box>

        {/* Selected / Total Count */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            pr: 2,
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
            minWidth: 'fit-content',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontWeight: 'bold',
              fontSize: '16px',
              color: 'green',
            }}
          >
            <CheckBoxIcon fontSize="small" />
            Selected Row: {selectedRows.length}
          </Box>

          <Box
            sx={{
              fontWeight: 'bold',
              fontSize: '16px',
            }}
          >
            Total Count: {filteredRows.length}
          </Box>
        </Box>
      </Box>
      
      <Paper
        sx={{
          width: "95%",
          overflow: "hidden",
          margin: "10px auto", // Center the Paper
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          padding: "20px",
          position: "relative",
        }}
      >
        <TableContainer sx={{ maxHeight: 480, overflowX: 'auto' }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow sx={{ "& > th": { padding: "2px" } }}>
                <TableCell padding="checkbox" sx={{ backgroundColor: "#dddbff" }}>
                  <Checkbox
                    indeterminate={selectedRows.length > 0 && selectedRows.length < filteredRows.length}
                    checked={filteredRows.length > 0 && selectedRows.length === filteredRows.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={{
                      backgroundColor: "#dddbff",
                      color: "#333",
                      fontWeight: "600",
                      padding: "4px 2px 6px 5px !important",
                      border: "0.5px solid #ddd",
                      borderBottom: "2px solid #ddd",
                      width: column.width || 'auto', // Use the column width if provided
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ borderBottom: "0.5px solid #ddd" }}
                    >
                      {column.label}
                    </Typography>
                    {column.id === 'BookNo' && (
                      <TextField
                        size="small"
                        variant="outlined"
                        // placeholder={`Search ${column.label}`}
                        value={searchTerms?.BookNo || ''}
                        onChange={(e) =>
                          handleSearchChange(column.id, e.target.value)
                        }
                        sx={{
                          mt: 1,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#f9f9f9",
                            fontSize: "0.7rem",
                            height: "26px",
                          },
                        }}
                      />
                    )}
                    {column.id !== 'BookNo' && (
                      <TextField
                        size="small"
                        variant="outlined"
                        onChange={(e) =>
                          handleSearchChange(column.id, e.target.value)
                        }
                        sx={{
                          mt: 1,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#f9f9f9",
                            fontSize: "1.2rem",
                            height: "26px",
                          },
                        }}
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = isSelected(index);
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={index}
                      onDoubleClick={() => handleRowDoubleClick(row)}
                      selected={isItemSelected}

                      sx={{
                        "& > td": {
                          padding: "0px",
                          border: "0.5px solid #ddd",
                        },
                        "&:hover": {
                          backgroundColor: "#f1f1f1",
                          cursor: "pointer",
                        },
                        transition: "background-color 0.3s ease",
                      }}
                    >

                      <TableCell padding="checkbox" sx={{
                        textAlign: 'center',
                        '& .MuiCheckbox-root': {
                          padding: '0',
                          display: 'flex',
                          margin: '0'
                        }
                      }}>
                        <Checkbox
                          checked={isSelected(row.BillCycle)}
                          onClick={(event) => handleCheckboxClick(event, row.BillCycle)}
                        />
                      </TableCell>
                      {CountryJsx && <CountryJsx row={row} ind={index} />}
                      {CountryJsx
                        ? columns.slice(1).map((column) => {
                          const value = row[column.id];
                          const backgroundColor = getCellBackgroundColor(value);

                          return (
                            <Tooltip
                              title={value === 'B' ? `Resident Name: ${row.ResidentName || 'N/A'}\nBook No: ${row.BookNo || 'N/A'}` : ''}
                              arrow
                              placement="top"
                            >
                              <TableCell
                                key={column.id}
                                align={column.align}
                                sx={{
                                  backgroundColor: backgroundColor,
                                  color: backgroundColor ? '#ffffff' : 'inherit',
                                  fontWeight: backgroundColor ? 'bold' : 'normal',
                                  width: column.width || 'auto',
                                  textAlign: column.align, // Set text alignment based on column config
                                }}
                              >
                                {value}
                              </TableCell>
                            </Tooltip>
                          );
                        })

                        : columns.map((column) => {
                          const value = row[column.id];
                          const backgroundColor = getCellBackgroundColor(value);

                          return (
                            <Tooltip
                              title={value === 'B' ? `Resident Name: ${row.ResidentName ? row.ResidentName : 'N/A'}\nBook No: ${row.BookNo ? row.BookNo : 'N/A'}` : ''}
                              arrow
                              placement="top"
                            >
                              <TableCell
                                key={column.id}
                                align={column.align}
                                sx={{
                                  backgroundColor: backgroundColor,
                                  color: backgroundColor ? '#ffffff' : 'inherit',
                                  fontWeight: backgroundColor ? 'bold' : 'normal',
                                  verticalAlign: 'middle',
                                  width: column.width || 'auto',
                                  textAlign: column.align, // Set text alignment based on column config
                                  padding: '0px',
                                  paddingLeft: column.align === 'left' ? '8px' : '0px',
                                  paddingRight: column.align === 'right' ? '8px' : '0px',
                                }}
                              >
                                {renderCell(index, column.id, value?.BedStatus || value || '', row[`${column.id}_original`], column.align)}
                              </TableCell>
                            </Tooltip>
                          );
                        })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        {isLoading && (
          <Box sx={{
            position: "absolute",
            top: 100,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            // zIndex: 1,
          }}>
            <CircularProgress sx={{ color: "#635bff" }} />
          </Box>
        )}

        <TablePagination
          rowsPerPageOptions={[50, 100, 300, 500, 1000]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          handlePageChange={handlePageChange}
          onPageChange={(event, newPage) => handlePageChange(newPage)}
          onRowsPerPageChange={(event) => {
            const newRowsPerPage = parseInt(event.target.value, 10);
            setRowsPerPage(newRowsPerPage);
            setPage(0);
          }}
          sx={{
            backgroundColor: "#dddbff",
            borderTop: "1px solid #ddd",
            color: "#333",
            borderRadius: "0 0 12px 12px",
            "& .MuiToolbar-root": {
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              width: "100%",

              flexWrap: "wrap",
            },
            "& .MuiTablePagination-displayedRows": {
              display: "none",
            },
            "& .MuiTablePagination-actions": {
              display: "none",
            },

            "& .MuiTablePagination-selectLabel": {
              marginBottom: 2,
            },
            "& .MuiTablePagination-select": {
              marginBottom: 0,
            },
          }}
          ActionsComponent={({ onPageChange }) => (
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                justifyContent: "center",
                marginLeft: "16px",
              }}
            >
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(event, newPage) => handlePageChange(newPage - 1)}
                showFirstButton
                showLastButton
                sx={{
                  "& .MuiPaginationItem-root.Mui-selected": {
                    backgroundColor: "#635bff",
                    color: "#ffffff",
                  },
                }}
              />
            </Stack>
          )}
        />
      </Paper>
      {!isLoading && rows.length === 0 && (
        <Box sx={{
          width: "90%",
          margin: "20px auto",
          display: "flex",
          justifyContent: "center",
          color: "red",
        }}>
          No Pending Record Found...
        </Box>
      )}
    </Box>
  );
};

export default TableComponentPending;